class predictiveSearch extends HTMLElement {
  constructor() {
    super();
    // console.log('predictive search init');
    this.includeCollections =
      this.dataset.includeCollections === "true" ? true : false;
    this.excludedCollections = this.dataset.excludedCollections
      ? this.dataset.excludedCollections.split(",")
      : [];
    this.translationNoResults = this.dataset.translationNoResults;
    this.translationCollection = this.dataset.translationCollection;

    this.apiLock = {
      locked: false,
      set lock(state) {
        this.locked = state;
        // console.log('apiLock:', state);
      },
    };

    this.inputBottomBorder = {
      round: () => {
        this.input.classList.remove("search-results--open");
      },
      square: () => {
        this.input.classList.add("search-results--open");
      },
    };
  }

  /**
   * Builds a result list item HTML string. Reused for collections, products, and pages.
   * @param {Object} opts
   * @param {string} opts.href - Link URL
   * @param {string} [opts.imgSrc] - Image URL (omit for no image, e.g. pages)
   * @param {string} [opts.imgAlt] - Image alt text
   * @param {string} opts.caption - Small label (e.g. "Brand", vendor, "Page")
   * @param {string} opts.title - Main title text
   * @param {number} opts.optionId - For id="ps-option-{optionId}"
   * @param {string} [opts.liClass] - Optional li class (e.g. "collection-item")
   * @returns {string} HTML string for one <li>
   */
  buildResultListItem({ href, imgSrc, imgAlt, caption, title, optionId, liClass = "" }) {
    const imgBlock = imgSrc
      ? `<div class="h-[60px] w-[60px] rounded items-center flex"><img class="rounded w-[60px] h-[60px] object-contain" src="${imgSrc}&width=60" alt="${imgAlt || title}"></div>`
      : "";
    const liClasses = ["transition-colors duration-200 hover:bg-[var(--color-secondary-100)]", liClass].filter(Boolean).join(" ");
    return `<li role="option" id="ps-option-${optionId}" class="${liClasses}" tabindex="-1"><a class="flex p-[16px] gap-3 items-center" href="${href}" class="search-result-link">${imgBlock}<div><p class="text-caption">${caption}</p><p class="text-s font-bold">${title}</p></div></a></li>`;
  }

  connectedCallback() {
    this.resultList = this.querySelector("#resultList");
    this.input = this.querySelector("#search-input");
    this.closeX = this.querySelector("#closeX");
    this.searchIcon = this.querySelector("#input-search-icon");

    this.searchIcon.addEventListener("click", () => {
      this.input.focus();
    });

    // close search on outside click
    window.addEventListener("click", (e) => {
      if (
        this.resultList &&
        !this.resultList.contains(e.target) &&
        !this.input.contains(e.target)
      ) {
        this.input.value = "";
        this.emptyDropdown();
        this.hideCloseX();
      }
    });

    // close search on Escape-keyup
    window.addEventListener("keyup", (e) => {
      if (e.key === "Escape") {
        this.input.value = "";
        this.emptyDropdown();
        this.hideCloseX();
      }
    });

    // enable keyboard navigation
    window.addEventListener("keyup", (e) => {
      this.resultListItems = Array.from(this.resultList.querySelectorAll("li"));

      if (e.code === "ArrowDown") {
        if (
          this.resultListItems.length === 1 &&
          this.resultListItems[0].innerHTML.includes(this.translationNoResults)
        ) {
          return;
        }

        let selected = this.resultList.querySelectorAll(
          ".search-result_selected"
        )[0];

        if (!selected && this.resultListItems.length > 0) {
          this.resultListItems[0].classList.add("search-result_selected");
          this.resultListItems[0].focus({ preventScroll: true });
          selected = this.resultListItems[0];
        } else if (selected && selected.nextElementSibling) {
          selected.classList.remove("search-result_selected");
          selected.nextElementSibling.classList.add("search-result_selected");
          selected.nextElementSibling.focus({ preventScroll: true });
        }
      }

      if (e.code === "ArrowUp") {
        let selected = this.resultList.querySelectorAll(
          ".search-result_selected"
        )[0];

        if (!selected) {
          return;
        } else {
          selected.classList.remove("search-result_selected");
          if (selected.previousElementSibling) {
            selected.previousElementSibling.classList.add(
              "search-result_selected"
            );
            selected.previousElementSibling.focus({ preventScroll: true });
          }
        }
      }
    });

    window.addEventListener("keyup", (e) => {
      if (e.code === "Enter") {
        let selected = this.resultList.querySelectorAll(
          ".search-result_selected"
        )[0];
        if (selected) {
          selected.querySelector("a").click();
        }
      }
    });

    this.input.addEventListener(
      "input",
      function (e) {
        // console.log('e: ', e);
        if (e.target.value.length === 0) {
          this.emptyDropdown();
          this.hideCloseX();
        }

        if (e.target.value.length >= 3) {
          this.showCloseX();
        }

        if (e.target.value.length < 3 || this.apiLock.locked) {
          return;
        }

        this.apiLock.lock = true;

        setTimeout(() => {
          this.callPredictiveSearchApi();
        }, 500);
      }.bind(this)
    );

    this.emptyDropdown();
    this.hideCloseX();
  }

  callPredictiveSearchApi() {
    if (this.input.value.length === 0) {
      this.apiLock.lock = false;
      return;
    }

    let query = encodeURI(this.input.value.replace(/[^A-Za-z0-9+]/gm, " "));
    // console.log('query:', query);

    if (!query || query === "") {
      this.apiLock.lock = false;

      return;
    }

    if (query.match(/(%20){3,}/g)) {
      this.apiLock.lock = false;
      this.showNoResultUI();

      return;
    }

    fetch(
      `/search/suggest.json?q=${query}&resources[type]=product,collection,page&resources[options][unavailable_products]=last`
    )
      .then((response) => response.json())
      .then((suggestions) => {
        if (suggestions) {
          let results = suggestions.resources.results;
          console.log(results);

          this.buildDropdown(results);
        }
      })
      .catch((err) => {
        console.error(err);
      });

    this.apiLock.lock = false;
  }

  buildDropdown(results) {
    this.emptyDropdown();

    let totalResultsToShow = 0;
    // a11y: sequential option ids for aria-activedescendant
    let optionIndex = 0;

    if (
      results.collections.length === 0 &&
      results.products.length === 0 &&
      results.pages.length === 0
    ) {
      this.showNoResultUI();
      return;
    }

    // adding collections to dropdown
    if (this.includeCollections) {
      for (let i = 0; i < results.collections.length; i++) {
        const result = results.collections[i];
        let titleLowerCase = result.title.toLowerCase();
        const handle = result.handle;

        if (
          titleLowerCase.includes("test") ||
          titleLowerCase.includes("home page") ||
          this.excludedCollections.includes(handle)
        ) {
          continue;
        }

        const listItem = this.buildResultListItem({
          href: result.url,
          imgSrc: result.featured_image?.url,
          imgAlt: `${this.translationCollection}: ${result.title}`,
          caption: this.translationCollection,
          title: result.title,
          optionId: optionIndex++,
          liClass: "collection-item",
        });
        this.resultList.innerHTML += listItem;

        totalResultsToShow++;
      }
    }

    // adding products to dropdown
    for (let i = 0; i < results.products.length; i++) {
      const result = results.products[i];
      const tags = result.tags;
      let isTestProduct = false;
      let listItem;
      let titleLowerCase = result.title.toLowerCase();

      // ignore test products
      if (titleLowerCase.includes("test")) {
        continue;
      }

      // ignore per tag
      for (let i = 0; i < tags.length; i++) {
        const tag = tags[i].toLowerCase();
        if (tag.includes("test")) {
          isTestProduct = true;
        }
      }

      if (isTestProduct || tags.includes("#nosearch")) {
        continue;
      }

      listItem = this.buildResultListItem({
        href: result.url,
        imgSrc: result.image,
        imgAlt: result.title,
        caption: result.vendor,
        title: result.title,
        optionId: optionIndex++,
      });
      this.resultList.innerHTML += listItem;

      totalResultsToShow++;
    }

    // adding pages to dropdown
    for (let i = 0; i < results.pages.length; i++) {
      const result = results.pages[i];
      let titleLowerCase = result.title.toLowerCase();

      if (titleLowerCase.includes("test")) {
        continue;
      }

      const listItem = this.buildResultListItem({
        href: result.url,
        caption: "Page",
        title: result.title,
        optionId: optionIndex++,
      });
      this.resultList.innerHTML += listItem;

      totalResultsToShow++;
    }

    this.inputBottomBorder.square();

    if (totalResultsToShow === 0) {
      this.showNoResultUI();
    }

    if (totalResultsToShow > 5) {
      this.resultList.classList.add("show-search-scrollbar");
    }
  }

  emptyDropdown() {
    if (!this.resultList) {
      return;
    }
    while (this.resultList.hasChildNodes()) {
      this.resultList.removeChild(this.resultList.firstChild);
    }

    this.inputBottomBorder.round();
    this.resultList.classList.remove("show-search-scrollbar");
  }

  showCloseX() {
    this.closeX.style.display = "block";
  }

  hideCloseX() {
    this.closeX.style.display = "none";
  }

  showNoResultUI() {
    this.emptyDropdown();
      let listItem = `<li class="p-[16px] text-s" role="option" id="ps-option-${Date.now()}" tabindex="-1"><p>${this.translationNoResults}</p></li>`;
    this.resultList.classList.remove("show-search-scrollbar");
    this.inputBottomBorder.square();
    this.resultList.innerHTML += listItem;
  }
}

customElements.define("predictive-search", predictiveSearch);
